import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { AuthModalProvider } from "./context/AuthModalContext";
import { CreditsProvider } from "./context/CreditsContext";
import { Toaster } from "./components/ui/sonner";
import AuthModal from "./components/AuthModal";
import ForgotPasswordModal from "./components/ForgotPasswordModal";
import ErrorBoundary from "./components/ErrorBoundary";
import RequireAuth from "./components/RequireAuth";
import AdminLayout from "./components/AdminLayout";
import Landing from "./pages/Landing";
import Home from "./pages/Home";
import Workspace from "./pages/Workspace";
import Billing from "./pages/Billing";
import Settings from "./pages/Settings";
import PublishedApp from "./pages/PublishedApp";
import SharedApp from "./pages/SharedApp";
import About from "./pages/About";
import Contact from "./pages/Contact";
import CancellationRefund from "./pages/CancellationRefund";
import DataDeletion from "./pages/DataDeletion";
import AdminOverview from "./pages/admin/AdminOverview";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminProjects from "./pages/admin/AdminProjects";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminPricing from "./pages/admin/AdminPricing";
import AdminDomains from "./pages/admin/AdminDomains";
import AdminCredits from "./pages/admin/AdminCredits";
import AdminContactInbox from "./pages/admin/AdminContactInbox";
import AdminAuditLog from "./pages/admin/AdminAuditLog";
import { api } from "./lib/api";
import { isPlatformHost } from "./lib/platform";
import GetApp from "./pages/GetApp";
import Privacy from "./pages/Privacy";
import AdminMobileApp from "./pages/admin/AdminMobileApp";


// A verified custom domain pointing here serves that project's published app at the root.
const Root = () => {
  const host = window.location.hostname;
  const [slug, setSlug] = React.useState(isPlatformHost(host) ? false : null);
  React.useEffect(() => {
    if (slug !== null) return;
    api.resolveHost(host).then((r) => setSlug(r.custom ? r.slug : false)).catch(() => setSlug(false));
  }, [host, slug]);
  if (slug === null) return <div className="min-h-screen bg-[#F5F8FC]" />;
  return slug ? <PublishedApp slugOverride={slug} /> : <Landing />;
};

const guard = (el) => <RequireAuth><ErrorBoundary>{el}</ErrorBoundary></RequireAuth>;
const safe = (el) => <ErrorBoundary>{el}</ErrorBoundary>;

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CreditsProvider>
            <AuthModalProvider>
              <Routes>
                <Route path="/" element={<Root />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/refund-policy" element={<CancellationRefund />} />
                <Route path="/cancellation-refund" element={<CancellationRefund />} />
                <Route path="/data-deletion" element={<DataDeletion />} />
                <Route path="/get-app" element={safe(<GetApp />)} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/p/:slug" element={safe(<PublishedApp />)} />
                <Route path="/s/:token" element={safe(<SharedApp />)} />
                <Route path="/home" element={guard(<Home />)} />
                <Route path="/project/:id" element={guard(<Workspace />)} />
                <Route path="/billing" element={guard(<Billing />)} />
                <Route path="/settings" element={guard(<Settings />)} />
                <Route path="/dashboard/*" element={<Navigate to="/home" replace />} />
                <Route path="/admin" element={<RequireAuth><AdminLayout /></RequireAuth>}>
                  <Route index element={safe(<AdminOverview />)} />
                  <Route path="users" element={safe(<AdminUsers />)} />
                  <Route path="projects" element={safe(<AdminProjects />)} />
                  <Route path="orders" element={safe(<AdminOrders />)} />
                  <Route path="pricing" element={safe(<AdminPricing />)} />
                  <Route path="domains" element={safe(<AdminDomains />)} />
                  <Route path="mobile-app" element={safe(<AdminMobileApp />)} />
                  <Route path="credits" element={safe(<AdminCredits />)} />
                  <Route path="contact" element={safe(<AdminContactInbox />)} />
                  <Route path="audit-log" element={safe(<AdminAuditLog />)} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <AuthModal />
              <ForgotPasswordModal />
              <Toaster theme="dark" position="bottom-right" richColors />
            </AuthModalProvider>
          </CreditsProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
